/**
 * MergeContractsPage (T3-075 / T3-076 / T3-088)
 * Admin-only standalone screen for consolidating duplicate Historical (pre-go-live / take-on)
 * contracts. Two tabs, mirroring the Approvals & Verifications screen's Pending/Completed framing:
 *   - "Duplicates" (pending): Historical stubs sharing external reference + product + currency —
 *     records likely created in error. Pick which to keep and merge the others in, amend a record,
 *     or delete one that nothing is linked to.
 *   - "Actioned" (completed): a newest-first log of merges + amendments from the audit trail.
 *     T3-088: rows are no longer dead ends — each carries an inline Amend + a link to the contract,
 *     because the record an entry refers to is usually still a live amendable row in Duplicates.
 *
 * Backed by:
 *   GET    /contracts/historical-duplicate-groups     (fn_contract_historical_duplicate_groups)
 *   GET    /contracts/historical-actioned             (fn_contract_historical_actioned)
 *   GET    /contracts/settings/workflow               (tenant toggles — gate the three actions)
 *   POST   /contracts/:id/historical-merge            (via MergeHistoricalDialog)
 *   POST   /contracts/:id/historical-override-balance (via HistoricalBalanceOverrideDialog / "Amend")
 *   DELETE /contracts/:id/historical                  (sp_contract_delete_historical)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  MergeType as MergeIcon,
  Tune as AmendIcon,
  Refresh as RefreshIcon,
  ContentCopy as DuplicatesIcon,
  History as ActionedIcon,
  Delete as DeleteIcon,
  LinkOff as LinkOffIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { apiGet, apiDelete, extractApiData, extractApiError } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import { useNotification } from '../../contexts/NotificationContext';
import TabPanel from '../../components/common/TabPanel';
import MergeHistoricalDialog from '../../components/common/MergeHistoricalDialog';
import HistoricalBalanceOverrideDialog from '../../components/common/HistoricalBalanceOverrideDialog';
import logger from '../../utils/logger';

const fmtMaturity = (d) => (d
  ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '-');
const fmtWhen = (d) => (d
  ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '-');
const money = (ccy, v) => (v == null ? '—' : `${ccy || ''} ${formatNumber(Number(v), 2)}`);

/**
 * A disabled MUI button emits no pointer events, so a Tooltip wrapped directly around one never
 * opens. Wrap in a span when (and only when) there is a reason to show — an enabled action gets no
 * dead wrapper. The reason string IS the tooltip: a blocked action always explains itself rather
 * than failing after the user has filled in a form.
 */
const GatedAction = ({ reason, children }) => (reason
  ? <Tooltip title={reason}><Box component="span" sx={{ display: 'inline-flex' }}>{children}</Box></Tooltip>
  : children);

const MergeContractsPage = () => {
  const notify = useNotification();
  const [activeTab, setActiveTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [actioned, setActioned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingActioned, setLoadingActioned] = useState(true);
  const [error, setError] = useState(null);
  const [mergeSurvivor, setMergeSurvivor] = useState(null);
  const [amendContract, setAmendContract] = useState(null);
  const [search, setSearch] = useState('');

  // T3-088: tenant toggles. The page previously read none, so with a toggle off the user filled in
  // an amount + a mandatory reason and only then ate a 403 from the API.
  const [allowHistoricalMerge, setAllowHistoricalMerge] = useState(false);
  const [allowHistoricalBalanceOverride, setAllowHistoricalBalanceOverride] = useState(false);
  const [allowDeleteHistorical, setAllowDeleteHistorical] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const loadDuplicates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet('/contracts/historical-duplicate-groups');
      setRows(extractApiData(res) || []);
    } catch (err) {
      logger.error('Error loading historical duplicate groups', { message: err.message });
      setError(extractApiError(err, 'Failed to load duplicate historical contracts'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadActioned = useCallback(async () => {
    setLoadingActioned(true);
    try {
      const res = await apiGet('/contracts/historical-actioned');
      setActioned(extractApiData(res) || []);
    } catch (err) {
      logger.error('Error loading historical actioned feed', { message: err.message });
    } finally {
      setLoadingActioned(false);
    }
  }, []);

  // Falling back to all-false is deliberate: a settings failure must disable the destructive
  // actions, never enable them.
  const loadSettings = useCallback(async () => {
    const res = await apiGet('/contracts/settings/workflow').catch((err) => {
      logger.error('Error loading workflow settings', { message: err.message });
      return { data: {} };
    });
    const d = res?.data || {};
    setAllowHistoricalMerge(d.allowHistoricalMerge === true);
    setAllowHistoricalBalanceOverride(d.allowHistoricalBalanceOverride === true);
    setAllowDeleteHistorical(d.allowDeleteHistorical === true);
  }, []);

  useEffect(() => {
    loadDuplicates();
    loadActioned();
    loadSettings();
  }, [loadDuplicates, loadActioned, loadSettings]);

  const refreshAll = () => { loadDuplicates(); loadActioned(); loadSettings(); };

  const term = search.trim().toLowerCase();

  const matchesDuplicate = useCallback((m) => {
    if (!term) return true;
    const dests = Array.isArray(m.rolled_into_contracts) ? m.rolled_into_contracts : [];
    return [
      m.reference,
      m.user_reference,
      m.investor_names,
      ...dests.map((d) => d.reference),
      ...dests.map((d) => d.user_reference),
    ].some((v) => v && String(v).toLowerCase().includes(term));
  }, [term]);

  // Group the flat duplicate rows by group_key for display, filtering members by the search term
  // first and then dropping any group left with nothing to show.
  const groups = useMemo(() => {
    const acc = {};
    rows.filter(matchesDuplicate).forEach((r) => {
      (acc[r.group_key] = acc[r.group_key] || []).push(r);
    });
    return acc;
  }, [rows, matchesDuplicate]);
  const groupKeys = Object.keys(groups);

  const filteredActioned = useMemo(() => actioned.filter((a) => {
    if (!term) return true;
    return [a.reference, a.user_reference, a.loser_ref, a.reason, a.changed_by]
      .some((v) => v && String(v).toLowerCase().includes(term));
  }), [actioned, term]);

  /**
   * Single source of truth for the three row actions. A non-null reason means "disabled", and the
   * string is shown as the tooltip. Merged-away is checked first so the survivor gets named.
   *
   * The merged-away arm is defence in depth: merged rows are filtered out of the Duplicates list by
   * fn_contract_historical_duplicate_groups, so it only fires when the list has gone stale — another
   * admin merging in a parallel session between page load and click. The DB guards remain the
   * authority (sp_contract_historical_override_balance / _amend_investor still raise).
   */
  const rowGate = useCallback((m) => {
    const mergedAway = m.merged_into_contract_id != null;
    const survivor = m.merged_into_reference || (mergedAway ? `#${m.merged_into_contract_id}` : null);
    const deps = Number(m.dependent_count) || 0;
    return {
      mergeReason: mergedAway
        ? `Already merged into ${survivor} — it cannot be a survivor.`
        : (!allowHistoricalMerge
          ? 'Merging historical contracts is not enabled for this tenant (Tenant Settings).'
          : null),
      amendReason: mergedAway
        ? `Merged into ${survivor} — a merged-away record cannot be amended.`
        : (!allowHistoricalBalanceOverride
          ? 'Amending historical contracts is not enabled for this tenant (Tenant Settings).'
          : null),
      deleteReason: !allowDeleteHistorical
        ? 'Deleting historical contracts is not enabled for this tenant (Tenant Settings).'
        : (mergedAway
          ? `Merged into ${survivor} — resolve the merge instead of deleting.`
          : (deps > 0
            ? `Referenced as the rollover source by ${deps} contract(s) — detach those rollovers first.`
            : null)),
    };
  }, [allowHistoricalMerge, allowHistoricalBalanceOverride, allowDeleteHistorical]);

  /**
   * Gate for the Actioned tab's inline Amend. Same shape as rowGate's amendReason, plus the two
   * states only this tab can reach: the audit entry outlives the record, so it may point at one
   * since deleted (contract_status NULL) or one merged away after the fact.
   */
  const actionedAmendReason = useCallback((a) => {
    if (!a.contract_id || a.contract_status == null) {
      return 'This record no longer exists — it has since been deleted.';
    }
    if (a.merged_into_contract_id != null) {
      return `Merged into ${a.loser_ref || `#${a.merged_into_contract_id}`} — a merged-away record cannot be amended.`;
    }
    if (a.contract_status !== 'Historical') {
      return `This contract is now ${a.contract_status} — amending is only allowed on Historical records.`;
    }
    if (!allowHistoricalBalanceOverride) {
      return 'Amending historical contracts is not enabled for this tenant (Tenant Settings).';
    }
    return null;
  }, [allowHistoricalBalanceOverride]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await apiDelete(`/contracts/${deleteTarget.id}/historical`);
      notify.success(`Deleted historical contract "${deleteTarget.user_reference || deleteTarget.reference}".`);
      setDeleteTarget(null);
      refreshAll();
    } catch (err) {
      logger.error('Error deleting historical contract', { message: err.message, status: err.response?.status });
      // Surface the server message verbatim — the dependent_count pre-check can be stale.
      setDeleteError(err.response?.data?.error?.message
        || err.response?.data?.message
        || err.message
        || 'Failed to delete historical contract.');
    } finally {
      setDeleting(false);
    }
  };

  const renderInvestors = (m) => {
    if (!m.investor_names) {
      return <Typography variant="caption" color="textSecondary">No investor recorded</Typography>;
    }
    return (
      <Tooltip title={m.investor_names}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m.investor_names}
          </Box>
          {Number(m.investor_count) > 1 && (
            <Chip size="small" variant="outlined" label={m.investor_count} sx={{ flexShrink: 0 }} />
          )}
        </Box>
      </Tooltip>
    );
  };

  const renderRolledInto = (m) => {
    const dests = Array.isArray(m.rolled_into_contracts) ? m.rolled_into_contracts : [];
    if (dests.length === 0) {
      return (
        <Tooltip title="Nothing rolls from this record yet — it is not linked to any contract.">
          <Chip size="small" variant="outlined" icon={<LinkOffIcon />} label="Not linked" />
        </Tooltip>
      );
    }
    const shown = dests.slice(0, 2);
    const rest = dests.slice(2);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        {shown.map((d) => (
          <Tooltip
            key={d.id}
            title={[d.user_reference, d.status, money(m.currency_code, d.rolled_amount)].filter(Boolean).join(' · ')}
          >
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              clickable
              component={RouterLink}
              to={`/contracts/${d.id}`}
              label={`${d.reference} · ${formatNumber(Number(d.rolled_amount), 2)}`}
            />
          </Tooltip>
        ))}
        {rest.length > 0 && (
          <Tooltip title={rest.map((d) => `${d.reference} · ${money(m.currency_code, d.rolled_amount)}`).join('\n')}>
            <Chip size="small" variant="outlined" label={`+${rest.length}`} />
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderAmended = (m) => {
    const hasHistory = m.last_amended_at != null;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
        <span>{m.allocatable_override != null ? money(m.currency_code, m.allocatable_override) : '—'}</span>
        {hasHistory && (
          <Tooltip
            title={`Amended ${fmtWhen(m.last_amended_at)}${m.last_amended_by ? ` by ${m.last_amended_by}` : ''}${m.last_amend_reason ? ` — reason: ${m.last_amend_reason}` : ''}`}
          >
            <ActionedIcon fontSize="inherit" color="action" sx={{ cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" fontWeight="bold">Merge Historical Contracts</Typography>
        <Button startIcon={<RefreshIcon />} onClick={refreshAll} disabled={loading || loadingActioned}>Refresh</Button>
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Consolidate duplicate historical (pre-go-live) contracts created during take-on. Pick the record to
        keep and merge the others into it, amend a record&apos;s allocatable balance / external reference, or
        delete one that nothing is linked to. All three actions are admin-only and must be enabled per
        tenant in Tenant Settings — an action that is unavailable is shown disabled with the reason.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TextField
        size="small"
        fullWidth
        placeholder="Search by system reference, external reference, investor name or linked contract…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          endAdornment: search
            ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
            : null,
        }}
      />

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth">
          <Tab
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DuplicatesIcon fontSize="small" />
                <span>Duplicates</span>
                {rows.length > 0 && <Chip label={rows.length} size="small" color="primary" />}
              </Box>
            )}
          />
          <Tab
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActionedIcon fontSize="small" />
                <span>Actioned</span>
                {actioned.length > 0 && <Chip label={actioned.length} size="small" color="default" />}
              </Box>
            )}
          />
        </Tabs>
      </Paper>

      {/* ---- Duplicates tab ---- */}
      <TabPanel value={activeTab} index={0} padding={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Alert severity="success">No duplicate historical contracts found.</Alert>
        ) : groupKeys.length === 0 ? (
          <Alert severity="info">No duplicate historical contracts match &quot;{search}&quot;.</Alert>
        ) : (
          groupKeys.map((key) => {
            const members = groups[key];
            const head = members[0];
            return (
              <Paper key={key} variant="outlined" sx={{ mb: 3, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {head.user_reference || '(no external reference)'}
                  </Typography>
                  <Chip size="small" label={`${members.length} records`} color="primary" variant="outlined" />
                  <Typography variant="caption" color="textSecondary">
                    {[head.product_type, head.currency_code].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>System Ref</TableCell>
                        <TableCell align="right">Capital</TableCell>
                        <TableCell align="right">Amended</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Rolled amounts itemised against this record in the rollover source table. This figure can UNDERSTATE what actually rolls from here: a contract that records its rollover only at contract level, without a per-investor or itemised breakdown, contributes nothing to this total. Treat Rolls Into (every destination) and the Delete tooltip (the reference count) as the authoritative view of what is linked — not this column.">
                            <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>Allocated</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>Maturity</TableCell>
                        <TableCell>Investors</TableCell>
                        <TableCell>Rolls Into</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.map((m) => {
                        const g = rowGate(m);
                        return (
                          <TableRow key={m.id} hover>
                            <TableCell>{m.reference}</TableCell>
                            <TableCell align="right">{money(m.currency_code, m.capital_amount)}</TableCell>
                            <TableCell align="right">{renderAmended(m)}</TableCell>
                            <TableCell align="right">{money(m.currency_code, m.allocated_rolled_amount)}</TableCell>
                            <TableCell>{fmtMaturity(m.maturity_date)}</TableCell>
                            <TableCell sx={{ maxWidth: 160 }}>{renderInvestors(m)}</TableCell>
                            <TableCell sx={{ maxWidth: 190 }}>{renderRolledInto(m)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <GatedAction reason={g.mergeReason}>
                                  <Tooltip title={g.mergeReason ? '' : 'Keep this record and merge another into it'}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={!!g.mergeReason}
                                      onClick={() => setMergeSurvivor(m)}
                                      aria-label="Keep and merge into"
                                    >
                                      <MergeIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </GatedAction>
                                <GatedAction reason={g.amendReason}>
                                  <Tooltip title={g.amendReason ? '' : 'Amend allocatable balance / external reference'}>
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      disabled={!!g.amendReason}
                                      onClick={() => setAmendContract(m)}
                                      aria-label="Amend"
                                    >
                                      <AmendIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </GatedAction>
                                <GatedAction reason={g.deleteReason}>
                                  <Tooltip title={g.deleteReason ? '' : 'Delete this historical record'}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={!!g.deleteReason}
                                      onClick={() => { setDeleteError(null); setDeleteTarget(m); }}
                                      aria-label="Delete"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </GatedAction>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ mt: 1 }} />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  Merge makes that record the survivor; you then pick which other record to fold into it.
                  Amend changes its allocatable balance or external reference. Delete is available only
                  while nothing is linked to the record. The external reference is the group heading above,
                  so it is not repeated per row. Investor and destination cells truncate — hover for the
                  full list.
                </Typography>
              </Paper>
            );
          })
        )}
      </TabPanel>

      {/* ---- Actioned tab ---- */}
      <TabPanel value={activeTab} index={1} padding={0}>
        {loadingActioned ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : actioned.length === 0 ? (
          <Alert severity="info">No historical merges or amendments recorded yet.</Alert>
        ) : filteredActioned.length === 0 ? (
          <Alert severity="info">No actioned entries match &quot;{search}&quot;.</Alert>
        ) : (
          <Paper variant="outlined">
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>System Ref</TableCell>
                    <TableCell>External Ref</TableCell>
                    <TableCell>Detail</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredActioned.map((a) => {
                    const amendReason = actionedAmendReason(a);
                    return (
                      <TableRow key={a.audit_id} hover>
                        <TableCell>{fmtWhen(a.changed_at)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={a.action_type === 'merge' ? <MergeIcon /> : <AmendIcon />}
                            label={a.action_type === 'merge' ? 'Merge' : 'Amend'}
                            color={a.action_type === 'merge' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {a.contract_id && a.reference
                            ? (
                              <Box component={RouterLink} to={`/contracts/${a.contract_id}`} sx={{ color: 'primary.main' }}>
                                {a.reference}
                              </Box>
                            )
                            : (a.reference || `#${a.contract_id}`)}
                        </TableCell>
                        <TableCell>{a.user_reference || '—'}</TableCell>
                        <TableCell>
                          {a.action_type === 'merge'
                            ? `Merged in ${a.loser_ref || `#${a.loser_id}`}`
                            : [
                              (a.ref_before != null && a.ref_after != null && a.ref_before !== a.ref_after)
                                ? `Ref ${a.ref_before} → ${a.ref_after}` : null,
                              (a.override_after != null || a.override_before != null)
                                ? `Ceiling → ${a.override_after != null ? formatNumber(Number(a.override_after), 2) : 'cleared'}` : null,
                            ].filter(Boolean).join('; ') || 'Amended'}
                        </TableCell>
                        <TableCell>{a.reason || '—'}</TableCell>
                        <TableCell>{a.changed_by || '—'}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <GatedAction reason={amendReason}>
                              <Tooltip title={amendReason ? '' : 'Amend this record now'}>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  disabled={!!amendReason}
                                  onClick={() => setAmendContract({
                                    id: a.contract_id,
                                    reference: a.reference,
                                    user_reference: a.user_reference,
                                    currency_code: a.currency_code,
                                    allocatable_override: a.allocatable_override,
                                    product_type: a.product_type,
                                    is_multi_investor: a.is_multi_investor,
                                  })}
                                  aria-label="Amend"
                                >
                                  <AmendIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </GatedAction>
                            {a.contract_id && a.reference && (
                              <Tooltip title="Open contract">
                                <IconButton
                                  size="small"
                                  component={RouterLink}
                                  to={`/contracts/${a.contract_id}`}
                                  aria-label="Open contract"
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </TabPanel>

      <MergeHistoricalDialog
        open={!!mergeSurvivor}
        survivor={mergeSurvivor}
        onClose={() => setMergeSurvivor(null)}
        onMerged={({ loser, survivor }) => {
          notify.success(`Merged "${loser.user_reference || loser.reference}" into "${survivor.user_reference || survivor.reference}".`);
          setMergeSurvivor(null);
          refreshAll();
        }}
      />
      <HistoricalBalanceOverrideDialog
        open={!!amendContract}
        contract={amendContract}
        onClose={() => setAmendContract(null)}
        onSaved={() => {
          notify.success('Amendment applied.');
          setAmendContract(null);
          refreshAll();
        }}
      />

      {/* ---- Delete confirmation ---- */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => { if (!deleting) { setDeleteTarget(null); setDeleteError(null); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Historical Contract</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to permanently delete
            {' '}<strong>{deleteTarget?.reference}</strong>
            {deleteTarget?.user_reference ? ` (${deleteTarget.user_reference})` : ''}? This action cannot
            be undone. If this contract is referenced as the rolled-from source by another contract,
            the deletion will be refused.
          </DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteTarget(null); setDeleteError(null); }} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MergeContractsPage;
